import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ReactTyped } from "react-typed";
import "./About.css";
import { ENDPOINTS } from "../api/api";
import { useTranslation } from "react-i18next";
import "../i18n";

const About = () => {
  const { t, i18n } = useTranslation();
  const [aboutData,  setAboutData]  = useState(null);
  const [values,     setValues]     = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    const lang = (i18n.language || "en").split("-")[0].toLowerCase();
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [aboutRes, valuesRes, milestonesRes] = await Promise.all([
          fetch(ENDPOINTS.about),
          fetch(`${ENDPOINTS.values}?lang=${lang}`),
          fetch(`${ENDPOINTS.milestones}?lang=${lang}`),
        ]);
        setAboutData(await aboutRes.json());
        setValues(await valuesRes.json());
        setMilestones(await milestonesRes.json());
      } catch (err) {
        console.error("[About] Failed to load data:", err);
        setError(err.message);
      } finally { setLoading(false); }
    };
    fetchAll();
  }, [i18n.language]);

  if (loading) return <div className="ab-loading"><div className="ab-loading-spinner"></div></div>;
  if (error) return <div className="ab-loading"><p style={{ color: "#c3b996" }}><i className="fa-solid fa-triangle-exclamation"></i> Unable to load page content. Please try again.</p></div>;

  const mission = aboutData?.mission;

  return (
    <div className="ab-page">
      <section className="ab-hero">
        <div className="ab-hero-bg">
          <div className="ab-orb ab-orb--1"></div><div className="ab-orb ab-orb--2"></div><div className="ab-orb ab-orb--3"></div>
          <div className="ab-hero-grid"></div>
        </div>
        <div className="ab-hero-inner">
          <div className="ab-hero-left">
            <span className="ab-eyebrow"><i className="fa-solid fa-building"></i> {t("about.eyebrow")}</span>
            <h1 className="ab-hero-title">
              <ReactTyped strings={[t("about.title")]} typeSpeed={50} showCursor={false} />
            </h1>
            <p className="ab-hero-subtitle">Empowering businesses and innovators through <strong>modern software solutions</strong> crafted with excellence, precision, and purpose — built for Africa and the world.</p>
            <p className="ab-hero-tagline">At <strong className="ab-brand">Solviotech</strong>, we believe technology should <em>simplify, amplify, and inspire</em> — helping organisations unlock their true potential in the digital era.</p>
            <div className="ab-hero-actions">
              <NavLink to="/portfolio" className="ab-btn-primary"><i className="fa-solid fa-rocket"></i> See Our Work</NavLink>
              <NavLink to="/contact" className="ab-btn-secondary">Work With Us <i className="fa-solid fa-arrow-right"></i></NavLink>
            </div>
          </div>
          <div className="ab-hero-right">
            <div className="ab-hero-img-wrap">
              <img src={`${process.env.PUBLIC_URL}/about-illustration.jpg`} alt="About Solviotech" className="ab-hero-img" onError={(e) => { e.target.style.display = "none"; }} />
              <div className="ab-hero-img-overlay"></div>
              <div className="ab-float-badge ab-float-badge--1"><i className="fa-solid fa-star"></i><span>98% Satisfaction</span></div>
              <div className="ab-float-badge ab-float-badge--2"><i className="fa-solid fa-code"></i><span>50+ Projects</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="ab-mv-section">
        <div className="ab-container">
          <div className="ab-section-header">
            <span className="ab-section-eyebrow">Our Story</span>
            <h2 className="ab-section-title">{t("about.mission")} &amp; {t("about.vision")}</h2>
          </div>
          <div className="ab-mv-grid">
            <div className="ab-mv-card ab-mv-card--mission">
              <div className="ab-mv-card-icon"><i className="fa-solid fa-bullseye"></i></div>
              <div className="ab-mv-card-content">
                <span className="ab-mv-label">{t("about.mission")}</span>
                <h3 className="ab-mv-title">Built to Achieve Excellence</h3>
                <p className="ab-mv-text">{mission?.content?.[0] || "To empower startups, businesses, and innovators through smart, scalable, and elegant technology."}</p>
                <NavLink to="/services" className="ab-mv-link">Explore Our Services <i className="fa-solid fa-arrow-right"></i></NavLink>
              </div>
            </div>
            <div className="ab-mv-card ab-mv-card--vision">
              <div className="ab-mv-card-icon"><i className="fa-solid fa-eye"></i></div>
              <div className="ab-mv-card-content">
                <span className="ab-mv-label">{t("about.vision")}</span>
                <h3 className="ab-mv-title">Built to Inspire Growth</h3>
                <p className="ab-mv-text">{mission?.content?.[1] || "A world where businesses leverage digital intelligence to reach new heights."}</p>
                <NavLink to="/team" className="ab-mv-link">{t("about.team")} <i className="fa-solid fa-arrow-right"></i></NavLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {milestones.length > 0 && (
        <section className="ab-timeline-section">
          <div className="ab-container">
            <div className="ab-section-header">
              <span className="ab-section-eyebrow">{t("about.journey")}</span>
              <h2 className="ab-section-title">How We Got Here</h2>
              <p className="ab-section-subtitle">From a shared idea to a growing engineering company — the Solviotech story.</p>
            </div>
            <div className="ab-timeline">
              {milestones.map((m, i) => (
                <div key={m.id || i} className={`ab-milestone ${i % 2 === 0 ? "ab-milestone--left" : "ab-milestone--right"}`}>
                  <div className="ab-milestone-card">
                    <span className="ab-milestone-year">{m.year}</span>
                    <h3 className="ab-milestone-title">{m.title}</h3>
                    <p className="ab-milestone-desc">{m.desc}</p>
                  </div>
                  <div className="ab-milestone-dot"></div>
                </div>
              ))}
              <div className="ab-timeline-line"></div>
            </div>
          </div>
        </section>
      )}

      {values.length > 0 && (
        <section className="ab-values-section">
          <div className="ab-container">
            <div className="ab-section-header">
              <span className="ab-section-eyebrow">{t("about.values")}</span>
              <h2 className="ab-section-title">The Principles That Guide Us</h2>
              <p className="ab-section-subtitle">Every decision, every line of code, and every client interaction is driven by these core values.</p>
            </div>
            <div className="ab-values-grid">
              {values.map((v, i) => (
                <div className="ab-value-card" key={v.id || i}>
                  <div className="ab-value-icon"><i className={v.icon}></i></div>
                  <h3 className="ab-value-title">{v.title}</h3>
                  <p className="ab-value-desc">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="ab-cta-section">
        <div className="ab-cta-inner">
          <div className="ab-cta-orb"></div>
          <span className="ab-section-eyebrow"><i className="fa-solid fa-handshake"></i> {t("cta.eyebrow")}</span>
          <h2 className="ab-cta-title">{t("cta.title")}</h2>
          <p className="ab-cta-sub">{t("cta.sub")}</p>
          <div className="ab-cta-actions">
            <NavLink to="/contact" className="ab-btn-primary"><i className="fa-solid fa-paper-plane"></i> {t("cta.btn1")}</NavLink>
            <NavLink to="/services" className="ab-btn-secondary">Explore Services <i className="fa-solid fa-arrow-right"></i></NavLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
